import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchMediaKeys,
  fetchMediaUrls,
  fetchProctoringDetails,
  checkReportAvailability,
  recalculateAssessment,
  fetchProgressionTimeline,
  // Import the action type constant directly
  RESET_MEDIA_STATE
} from '../../actions'
import {
  Drawer,
  Button,
  Row,
  Col,
  Card,
  Typography,
  Badge,
  Divider,
  Image,
  Empty,
  Spin,
  Statistic,
  Select,
  Table,
  Tag,
  List,
  message,
  Modal,
  Tooltip,
  Dropdown,
  Menu,
} from 'antd'
import {
  EyeOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  GlobalOutlined,
  VideoCameraTwoTone,
  AudioTwoTone,
  AudioFilled,
  VideoCameraFilled,
  ReloadOutlined,
  DownOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import styled from 'styled-components'
import Avatar from 'components/Avatar'
import AudioVideoModal from '../../../../components/AudioVideoModal'
import ProgressionGraph from '../ActiveCollegeList/components/ProgressionGraph'
import { getReportByAssignment as getAIInterviewReportByAssignment } from '../../../AIInterview/aiInterviewAPI'
import {
  communicationMetrics,
  communicationVerdict,
  formatScore,
  isCommunicationParameter,
} from '../../../AIInterview/communicationAnalytics'
import { hasAptitudeScore, isAptitudeReport } from '../aptitudeSections'
import ProctoringReportPanel from './ProctoringReportPanel'
import {
  collectSnapshots,
  primarySession,
  collectIpAddresses,
} from './proctoringView'
import { SINGLE_DIAGNOSIS_ATTEMPT_MESSAGE, isSingleDiagnosisAttempt } from './reportDownloadGate'
import {
  ATTEMPT_STATUS,
  ATTEMPT_STATUS_TOOLTIP,
  isReportPreparingStatus,
  resolveAttemptStatus,
} from '../attemptStatus'
import { VERDICT_COLORS, effectiveVerdict } from '../../../AIInterview/aiInterviewVerdict'
import {
  describeInterviewEnding,
  describeScoreImpact,
} from './aiInterviewEnding'
import adminRequest from 'utils/adminRequest'
import axios from 'axios'

const { Title, Text } = Typography
const { Option } = Select

const ScoreCard = styled(Card)`
  text-align: center;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background-color: ${props => {
    // If it's a proficiency level
    if (typeof props.score === 'string') {
      // Use red background for Beginner, green for all others
      return props.score.toLowerCase() === 'beginner' ? '#FBDFE1' : '#BFF4E0'
    } else {
      // Original score-based coloring
      return parseFloat(props.score) <= 25 ? '#FBDFE1' : '#BFF4E0'
    }
  }};
  border: 2px solid
    ${props => {
    // If it's a proficiency level
    if (typeof props.score === 'string') {
      // Use red border for Beginner, green for all others
      return props.score.toLowerCase() === 'beginner' ? '#F54B56' : '#5DB594'
    } else {
      // Original score-based coloring
      return parseFloat(props.score) <= 25 ? '#F54B56' : '#5DB594'
    }
  }};
  height: 150px;
  display: flex;
  flex-direction: column;

  .ant-card-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: space-between;
  }
`

const ScoreValue = styled.div`
  font-family: 'Plus Jakarta Sans';
  font-weight: 700;
  font-size: 22px;
  line-height: 25px;
  letter-spacing: 0%;
  text-align: center;
  color: '#FFFFFF';
  margin: 12px 0;
`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const ScoreTitle = styled(Title)`
  margin-bottom: 0 !important;
  color: #333333; /* Change title color for better readability */
`

const SubText = styled(Text)`
  color: #666666;
  font-size: 12px;
  white-space: nowrap;
`
const TitleSection = styled.div`
  margin-bottom: 10px;
`

const ValueSection = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`

const SnapshotCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  height: 100%;

  .ant-card-body {
    padding: 12px;
  }

  .snapshot-image {
    width: 100%;
    height: 150px;
    object-fit: cover;
    border-radius: 4px;
  }

  .snapshot-info {
    margin-top: 8px;
  }
`

const ProctoringSection = styled.div`
  margin-top: 24px;
`

// `isProctoringDisabled`: pass true only when the assessment is known to have
// proctoring switched off. `allowProctoring` cannot say so — several callers
// hard-code it (true or false) regardless of the assessment.
const StudentReportModal = ({ visible, onClose, student, allowProctoring, isProctoringDisabled = false, isDiagnosis = false, enabledSections: enabledSectionsProp, assessmentType: assessmentTypeProp, headerExtra = null }) => {
  const dispatch = useDispatch()
  const [proctoringData, setProctoringData] = useState(null)
  const [proctoringLoading, setProctoringLoading] = useState(false)
  const [displayType, setDisplayType] = useState('snapshots') // New state for tracking the selected display type
  const [audioVideoVisible, setAudioVideoVisible] = useState(false)
  const [selectedMediaType, setSelectedMediaType] = useState(null)
  const [downloadingReport, setDownloadingReport] = useState(false)
  const [recalculating, setRecalculating] = useState(false)
  // Integrity report (same source as the PDF) — lifted from ProctoringReportPanel
  // so the "Proctoring Status" badge reflects the integrity band, not just the
  // old face-detection Good/Bad (which can disagree, e.g. review vs Good).
  const [integrityReport, setIntegrityReport] = useState(null)

  // For diagnosis mode: track which assessment is selected (drives proctoring, media, recalculate, etc.)
  const [selectedAssessmentId, setSelectedAssessmentId] = useState(null)

  // Progression graph data
  const [commProgression, setCommProgression] = useState([])
  const [aptProgression, setAptProgression] = useState([])
  const [commDelta, setCommDelta] = useState(null)
  const [aptDelta, setAptDelta] = useState(null)
  const [progressionLoading, setProgressionLoading] = useState(false)

  // Determine which progression graphs to show based on assessmentType prop
  const normalizedAssessmentType = (assessmentTypeProp || student?.assessmentType || '').toLowerCase()
  const isCommunicationType = ['communication', 'hinglish'].includes(normalizedAssessmentType)
  const isAptitudeType = normalizedAssessmentType === 'aptitude'
  const isAIInterviewType =
    normalizedAssessmentType.includes('ai_interview') ||
    normalizedAssessmentType.includes('aiinterview') ||
    normalizedAssessmentType === 'ai interview'

  // AI Interview report fetched lazily when the modal opens for an AI Interview row
  const [aiInterviewReport, setAiInterviewReport] = useState(null)
  const [aiInterviewLoading, setAiInterviewLoading] = useState(false)
  const [aiInterviewError, setAiInterviewError] = useState(null)

  // Cache fetched data per assessment ID while drawer is open
  const [cache, setCache] = useState({}) // { [assessmentId]: { proctoring, media, reportAvailable } }

  // Initialize selectedAssessmentId when drawer opens in diagnosis mode
  useEffect(() => {
    if (visible && isDiagnosis) {
      const defaultId = student?.assessmentAssignedId1 || student?.assessmentAssignedId2 || null
      setSelectedAssessmentId(defaultId)
    } else if (visible && !isDiagnosis) {
      setSelectedAssessmentId(student?.assessmentAssignedId || null)
    }
    if (!visible) {
      setSelectedAssessmentId(null)
      setCache({}) // Clear cache when drawer closes
    }
  }, [visible, isDiagnosis, student?.assessmentAssignedId, student?.assessmentAssignedId1, student?.assessmentAssignedId2])

  // The effective assessment ID used throughout the drawer
  const effectiveAssessmentId = isDiagnosis ? selectedAssessmentId : student?.assessmentAssignedId

  // Fetch the AI Interview report on demand. Cheap to retry — orchestrator
  // already memoises by sessionId so repeated opens are fine.
  useEffect(() => {
    if (!visible || !isAIInterviewType || !effectiveAssessmentId) {
      setAiInterviewReport(null)
      setAiInterviewError(null)
      return
    }
    let cancelled = false
    setAiInterviewLoading(true)
    setAiInterviewError(null)
    getAIInterviewReportByAssignment(effectiveAssessmentId)
      .then((resp) => {
        if (cancelled) return
        setAiInterviewReport(resp?.data?.data || resp?.data || null)
      })
      .catch((err) => {
        if (cancelled) return
        const detail =
          err?.response?.data?.message || err?.message || 'Failed to load AI Interview report'
        setAiInterviewError(detail)
      })
      .finally(() => {
        if (!cancelled) setAiInterviewLoading(false)
      })
    return () => { cancelled = true }
  }, [visible, isAIInterviewType, effectiveAssessmentId])

  // Access the media state from Redux
  const mediaState = useSelector(state => state.mediaState)
  const {
    loading: mediaLoading,
    audioAvailable,
    videoAvailable,
    reportAvailable,
    mediaUrls,
    error: mediaError
  } = mediaState


  // Calculate if report should be downloadable.
  // reportAvailable (from checkReportAvailability) is the authoritative signal —
  // it already requires scoresCalculated===true for normal types AND falls back
  // to "a finalized score exists" for AI Interview (whose PDF is built from the
  // score row, not the scores_calculated flag). The extra client-side
  // `scoresCalculated !== false` was double-gating and kept the button grey for
  // completed AI interviews whose flag lagged — drop it, trust the API.
  // A Diagnosis report is the paired result of both papers - block the PDF while
  // only one of the two has been attempted.
  const hasSingleDiagnosisAttempt = isSingleDiagnosisAttempt(student, isDiagnosis)
  // Submitted but nothing scored yet ("Preparing Report" / "Report Delayed",
  // see attemptStatus.js): the drawer shows a preparing state instead of a
  // half-empty report and the PDF stays locked. An AI Interview whose report
  // already carries a score or an ending banner is past that point.
  const attemptStatusKey = isDiagnosis ? null : resolveAttemptStatus(student)
  const isReportPreparing =
    isReportPreparingStatus(attemptStatusKey) &&
    !(aiInterviewReport?.score || aiInterviewReport?.partialInterview)
  const isReportDownloadable = reportAvailable && !hasSingleDiagnosisAttempt && !isReportPreparing
  const downloadReportTitle = hasSingleDiagnosisAttempt
    ? SINGLE_DIAGNOSIS_ATTEMPT_MESSAGE
    : isReportPreparing
      ? ATTEMPT_STATUS_TOOLTIP[attemptStatusKey]
      : isReportDownloadable
      ? 'Download assessment report'
      : 'Report is not available for this assessment yet'

  // Fetch the integrity report (same source as the PDF) so the Proctoring Status
  // badge can reflect the integrity band — not just the old face-detection
  // Good/Bad, which ignores gaze/read-aloud/interaction signals and can disagree
  // (e.g. PDF says Needs Review while the old badge says Good).
  useEffect(() => {
    if (!visible || !effectiveAssessmentId) {
      setIntegrityReport(null)
      return
    }
    let cancelled = false
    adminRequest
      .get(`/assessment/${effectiveAssessmentId}/proctoring-report`)
      .then((resp) => {
        if (cancelled) return
        setIntegrityReport(resp?.data?.data || resp?.data || null)
      })
      .catch(() => { if (!cancelled) setIntegrityReport(null) })
    return () => { cancelled = true }
  }, [visible, effectiveAssessmentId])

  // Map the integrity band to a Good/Bad-style verdict for the status badge.
  // Only 2 tiers exist now — review ⇒ flagged; clean ⇒ good; no_data/missing
  // ⇒ no verdict (fall back to the legacy face-detection signal).
  const integrityBand = integrityReport?.integrityBand
  const integrityFlagged = integrityBand === 'review'
  // AI Interview answers the reading check confirmed (interaction ids).
  const readingFlaggedIds = new Set(integrityReport?.summary?.readingFlaggedQuestionIds || [])

  // An on-call (phone) AI Interview has no camera, so it is never proctored;
  // its full call recording plays from the transcript head instead.
  // ponytail: admin-node's GET /ai-interview/report-by-assignment does not
  // send `deliveryMode` or `callRecording` yet, so both stay off until it does
  // (student-node's report-v2 reads them from ai_interview_config.stage_config
  // ->>'deliveryMode' and ai_interview_call_slots.recordings).
  const isCallInterview = isAIInterviewType && aiInterviewReport?.deliveryMode === 'call'
  const callRecordingUrl = aiInterviewReport?.callRecording?.url || null

  // Fetch progression timeline data
  useEffect(() => {
    if (!visible || !student?.email) {
      setCommProgression([])
      setAptProgression([])
      return
    }
    const loadProgression = async () => {
      setProgressionLoading(true)
      try {
        // Only fetch the relevant progression based on assessment type
        const fetchComm = !isAptitudeType
        const fetchApt = !isCommunicationType

        const [commResult, aptResult] = await Promise.all([
          fetchComm ? dispatch(fetchProgressionTimeline(student.email, 'communication', null, effectiveAssessmentId)) : Promise.resolve(null),
          fetchApt ? dispatch(fetchProgressionTimeline(student.email, 'aptitude', null, effectiveAssessmentId)) : Promise.resolve(null),
        ])
        const commTimeline = commResult ? (commResult?.timeline || (Array.isArray(commResult) ? commResult : [])) : []
        const aptTimeline = aptResult ? (aptResult?.timeline || (Array.isArray(aptResult) ? aptResult : [])) : []

        setCommProgression(commTimeline)
        setAptProgression(aptTimeline)
        setCommDelta(commResult?.delta?.assessment || null)
        setAptDelta(aptResult?.delta?.assessment || null)
      } catch (e) {
        console.error('Failed to load progression:', e)
      }
      setProgressionLoading(false)
    }
    loadProgression()
  }, [visible, student?.email, effectiveAssessmentId, dispatch])

  //console.log("student : ", student);

  // Fetch media and report availability when the drawer opens or the selected
  // Mix & Match part changes. Media is deliberately not cached here: it lives
  // in one Redux slot, so retaining the previous part's signed URL makes every
  // tab replay the first video that was opened.
  useEffect(() => {

    if (visible && effectiveAssessmentId) {
      // Clear the previous part before fetching this one's keys. Without this,
      // handleMediaView sees an old non-empty URL and skips URL generation for
      // the newly selected assessment.
      dispatch({ type: RESET_MEDIA_STATE })
      dispatch(fetchMediaKeys(effectiveAssessmentId))

      const rawStudentNumber = student?.studentNumber ?? student?.studentId ?? student?.id
      const studentNumber = (rawStudentNumber !== null && rawStudentNumber !== undefined && rawStudentNumber !== '-') ? rawStudentNumber : null

      if (studentNumber !== null) {
        dispatch(checkReportAvailability(effectiveAssessmentId, studentNumber))
      } else {
        console.warn('Student number or assessment ID is missing, cannot check report availability');
      }

    }

    // Reset media state when the component unmounts or drawer closes
    return () => {
      if (!visible) {
        // Dispatch the RESET_MEDIA_STATE action directly instead of using the action creator
        dispatch({ type: RESET_MEDIA_STATE })
      }
    }
    // Depends on the identifiers this effect actually reads, NOT on the whole
    // `student` object: a caller that rebuilds that object per render (a Mix &
    // Match float merges the active part's scores onto the candidate) would
    // otherwise re-run this on every render, and the setState below turns that
    // into an infinite update loop.
  }, [visible, effectiveAssessmentId, student?.studentNumber, student?.studentId, student?.id, dispatch])

  // For debugging - log media state changes
  useEffect(() => {
    //console.log('Media state updated:', mediaState)
  }, [mediaState])

  // Restore cached proctoring data when selected assessment changes
  useEffect(() => {
    if (effectiveAssessmentId && cache[effectiveAssessmentId]?.proctoringData) {
      setProctoringData(cache[effectiveAssessmentId].proctoringData)
    } else {
      setProctoringData(null)
    }
  }, [effectiveAssessmentId])

  const handleViewProctoring = async () => {
    try {
      if (!effectiveAssessmentId) {
        console.error('No assessment assigned ID found')
        return
      }

      // Return cached proctoring data if available
      if (cache[effectiveAssessmentId]?.proctoringData) {
        setProctoringData(cache[effectiveAssessmentId].proctoringData)
        return
      }

      setProctoringLoading(true)
      const data = await dispatch(
        fetchProctoringDetails(effectiveAssessmentId)
      )
      setProctoringData(data)
      // Cache the proctoring data
      setCache(prev => ({
        ...prev,
        [effectiveAssessmentId]: { ...prev[effectiveAssessmentId], proctoringData: data }
      }))
    } catch (error) {
      console.error('Failed to fetch proctoring details:', error)
      message.error('Failed to load proctoring data')
    } finally {
      setProctoringLoading(false)
    }
  }


  /**
   * Fetches the media keys (filenames) from the assessment proctor API
   * @returns {Promise<Array>} Array of file objects with filename and type
   */
  const getMediaKeys = async () => {
    try {
      const kvResp = await fetch(
        `${process.env.ADMIN_API_URL}/assessment/getAudioVideoProctoring?assessmentAssignedId=${effectiveAssessmentId}`
      )

      if (!kvResp.ok) throw new Error(`Failed to fetch keys (${kvResp.status})`)

      const kvData = await kvResp.json()
      // //console.log('Object keys response:', kvData)

      // Check if we have valid rows in the response
      if (!kvData.data?.rows || !Array.isArray(kvData.data.rows) || kvData.data.rows.length === 0) {
        message.warning('No media files found for this assessment')
        return []
      }

      const rows = kvData.data.rows
      // //console.log('Object keys:', rows)

      // Match known patterns for audio and video files
      // Audio patterns: 'audio', 'Paragraph_reading', 'etc.
      // Video patterns: 'video', 'Video_respose', 'etc.
      const audioPatterns = ['audio', 'paragraph_reading', 'paragraph', 'reading', 'ai_interview_answer']
      const videoPatterns = ['video', 'video_respose', 'webcam']

      // Find audio row by matching patterns (case insensitive)
      const audioRow = rows.find(r =>
        audioPatterns.some(pattern =>
          r.object_key.toLowerCase().includes(pattern.toLowerCase())
        )
      )
      const audioKey = audioRow?.object_key

      // //console.log('Audio key:', audioKey)

      // Find video key by matching patterns (case insensitive)
      const videoKey = rows.find(r =>
        videoPatterns.some(pattern =>
          r.object_key.toLowerCase().includes(pattern.toLowerCase())
        )
      )?.object_key

      // //console.log('Video key:', videoKey)

      // Build array of files to request URLs for
      const files = []
      // chapters: per-question jump points for AI Interview's session audio
      // (null for every other assessment type's audio row).
      if (audioKey) files.push({ filename: audioKey, type: 'audio', chapters: audioRow?.chapters || null })
      if (videoKey) files.push({ filename: videoKey, type: 'video' })

      // If no files matched our patterns, provide helpful error message
      if (!files.length) {
        console.warn('Could not identify audio or video files from:', rows.map(r => r.object_key).join(', '))
        message.warning('Could not identify audio or video files from the assessment')
      }

      return files
    } catch (error) {
      console.error('Error fetching media keys:', error)
      message.error('Failed to fetch media keys')
      return []
    }
  }

  /**
   * Fetches signed URLs for media files from the API
   * @param {Array} files - Array of file objects with filename and type
   * @returns {Promise<Object>} Object containing audio and video URLs
   */
  const getMediaUrls = async (files) => {
    if (!files || !Array.isArray(files) || files.length === 0) {
      console.warn('No files provided to get URLs for')
      return { audio: null, video: null }
    }

    try {
      const urlResp = await fetch(`${process.env.STD_API_URL}students/assessments/getMediaUrls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ files })
      })

      if (!urlResp.ok) {
        const errorText = await urlResp.text()
        throw new Error(`Failed to fetch media URLs: ${errorText}`)
      }

      const urlData = await urlResp.json()
      // //console.log('URL response:', urlData)

      // Validate response
      if (!urlData.data?.urls) {
        throw new Error('Invalid response format: missing data.urls')
      }

      const urls = urlData.data.urls

      // Extract the audio and video URLs using the file types
      const audioFile = files.find(f => f.type === 'audio')
      const videoFile = files.find(f => f.type === 'video')

      // The response from the API just has the filenames as keys without the audio_/video_ prefix
      const audioUrl = audioFile ? urls[audioFile.filename] || null : null
      const videoUrl = videoFile ? urls[videoFile.filename] || null : null

      // //console.log('Audio URL:', audioUrl)
      // //console.log('Video URL:', videoUrl)

      return { audio: audioUrl, video: videoUrl }
    } catch (error) {
      console.error('Error fetching media URLs:', error)
      message.error(`Failed to fetch media URLs: ${error.message}`)
      return { audio: null, video: null }
    }
  }

  const handleMediaView = async (mediaType) => {
    // Set modal visibility and selected media type
    setSelectedMediaType(mediaType)
    setAudioVideoVisible(true)

    if (!effectiveAssessmentId) {
      console.error('No assessment assigned ID found')
      message.error('No assessment assigned ID found')
      return
    }

    // If we already have the media files but not the URLs, fetch them
    if (mediaState.mediaFiles.length > 0 && (!mediaUrls.audio && !mediaUrls.video)) {
      try {
        await dispatch(fetchMediaUrls(mediaState.mediaFiles))
      } catch (error) {
        console.error('Error fetching media URLs:', error)
        message.error('Failed to fetch media URLs')
      }
    }

    // Verify that the requested media type is available
    if (mediaType === 'audio' && !audioAvailable) {
      message.warning('Audio recording is not available for this assessment')
    } else if (mediaType === 'video' && !videoAvailable) {
      message.warning('Video recording is not available for this assessment')
    }
  }

  // Add a handler to properly close the modal and reset state
  const handleAudioVideoModalClose = () => {
    setAudioVideoVisible(false)
    setSelectedMediaType(null)
    // Add a small delay to ensure the modal is fully closed before resetting
    setTimeout(() => {
      setSelectedMediaType(null)
    }, 100)
  }

  const formatDateTime = dateString => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
  }

  const formatIfCalculated = (value, suffix = '') => {
    if (student && student.scoresCalculated === false) return '-'
    const num = Number(value)
    return Number.isFinite(num) ? `${num.toFixed(2)}${suffix}` : `0.00${suffix}`
  }

  // Safe toFixed helper to prevent "toFixed is not a function" errors
  const safeToFixed = (value, decimals = 2, suffix = '') => {
    const num = Number(value)
    return Number.isFinite(num) ? `${num.toFixed(decimals)}${suffix}` : '-'
  }

  // Handle selection change
  const handleDisplayTypeChange = value => {
    setDisplayType(value)
  }

  // Process IP addresses - show unique IPs only
  const processIpAddresses = (ipAddresses, snapshots) => {
    if (!ipAddresses || !ipAddresses.length) return []

    // Check if all IPs are the same
    const firstIp = ipAddresses[0]?.join(', ') || ''
    const allSame = ipAddresses.every(ip => ip.join(', ') === firstIp)

    if (allSame) {
      // If all IPs are the same, return just one entry
      return [
        {
          key: 0,
          ip: firstIp,
          timestamp: snapshots[0]?.capturedAt
            ? formatDateTime(snapshots[0].capturedAt)
            : 'N/A',
          occurredAt: 'All sessions',
          count: ipAddresses.length,
        },
      ]
    } else {
      // Otherwise, find unique IPs with their timestamps
      const uniqueIps = new Map()

      ipAddresses.forEach((ip, index) => {
        const ipString = ip.join(', ')
        if (!uniqueIps.has(ipString)) {
          uniqueIps.set(ipString, {
            key: index,
            ip: ipString,
            timestamp: snapshots[index]?.capturedAt
              ? formatDateTime(snapshots[index].capturedAt)
              : 'N/A',
            occurredAt: `Session ${index + 1}`,
            count: 1,
          })
        } else {
          const existing = uniqueIps.get(ipString)
          existing.count++
          existing.occurredAt = `Multiple sessions`
        }
      })

      return Array.from(uniqueIps.values())
    }
  }

  // Check if this is a behavior assessment by looking for behaviorProficiencies.
  // Backend returns these fields as the status string (e.g. "PENDING") when the
  // student hasn't completed the assessment, so require an actual object.
  const hasBehaviorObject = (v) =>
    v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0
  const isBehaviorAssessment =
    hasBehaviorObject(student?.behaviorProficiencies) ||
    hasBehaviorObject(student?.behaviorScores) ||
    normalizedAssessmentType.includes('behavi')

  // Aptitude by its scores, or — when there are none yet — by its type.
  // See isAptitudeReport: an unscored drop-off used to be drawn as a language
  // report because the type was never consulted.
  const isAptitudeAssessment = isAptitudeReport(student, normalizedAssessmentType)
  const isRoleBasedAssessment = (student?.roleBasedScores && typeof student.roleBasedScores === 'object') ||
    (student?.assessmentType && (student.assessmentType.toLowerCase().includes('role_based') || student.assessmentType.toLowerCase().includes('rolebased')))
  const customScoresData = student?.customAssessmentScores || student?.customassessmentscores || student?.originalData?.customAssessmentScores || student?.originalData?.customassessmentscores
  const isCustomAssessment = (customScoresData && typeof customScoresData === 'object') ||
    (student?.assessmentType && student.assessmentType.toLowerCase().includes('custom'))


  const renderRoleBasedScoreCards = () => {
    // Support both roleBasedScores (direct) and sectionScores (from API)
    const roleBasedData = student?.roleBasedScores || student?.sectionScores
    if (!roleBasedData || typeof roleBasedData !== 'object') return null

    // Check if data has predefined keys (overallScore, mcqScore, etc.)
    const predefinedMapping = {
      overallScore: { title: 'Overall Score', subtitle: '(Weighted)' },
      mcqScore: { title: 'MCQ Score', subtitle: '(Multiple Choice)' },
      subjectiveScore: { title: 'Subjective Score', subtitle: '(Written Response)' },
      videoScore: { title: 'Video Score', subtitle: '(Video Response)' },
      codingScore: { title: 'Coding Score', subtitle: '(Programming)' }
    }

    const hasPredefinedKeys = Object.keys(predefinedMapping).some(k => roleBasedData.hasOwnProperty(k))

    let scoreEntries
    if (hasPredefinedKeys) {
      scoreEntries = Object.keys(predefinedMapping)
        .filter(key => roleBasedData.hasOwnProperty(key))
        .map(key => ({ key, title: predefinedMapping[key].title, subtitle: predefinedMapping[key].subtitle, value: roleBasedData[key] }))
    } else {
      // Dynamic section names from API sectionScores (e.g., "mcq question", "subjective question", "video response")
      const capitalize = (str) => str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      // Add overall score first
      const entries = [{ key: 'overall', title: 'Overall Score', subtitle: '(Average)', value: student?.totalScore }]
      Object.entries(roleBasedData).forEach(([key, value]) => {
        if (typeof value === 'number') {
          entries.push({ key, title: capitalize(key), subtitle: '', value })
        }
      })
      scoreEntries = entries.filter(e => e.value != null)
    }

    // Per-video question breakdown (visible whenever the candidate has answered
    // one or more video questions — matches what the PDF report shows).
    const videoDetailedScoresRaw = student?.videoDetailedScores ?? roleBasedData.videoDetailedScores
    const videoDetailedScores = Array.isArray(videoDetailedScoresRaw) ? videoDetailedScoresRaw : []
    const videoTotalQuestions = student?.videoTotalQuestions ?? roleBasedData.videoTotalQuestions ?? videoDetailedScores.length

    // Group scores into rows of 4
    const rows = chunkArray(scoreEntries, 4)

    return (
      <>
        {rows.map((rowEntries, rowIndex) => (
          <Row
            key={`rolebased-row-${rowIndex}`}
            gutter={16}
            style={{ marginBottom: rowIndex < rows.length - 1 ? 16 : 32 }}
          >
            {rowEntries.map(entry => (
                <Col key={entry.key} xs={24} sm={12} md={12} lg={6}>
                  <ScoreCard score={entry.value}>
                    <CardContent>
                      <TitleSection>
                        <ScoreTitle level={5}>{entry.title}</ScoreTitle>
                        {entry.subtitle && <SubText>{entry.subtitle}</SubText>}
                      </TitleSection>
                      <ValueSection>
                        <ScoreValue score={entry.value}>
                          {safeToFixed(entry.value)}
                        </ScoreValue>
                      </ValueSection>
                    </CardContent>
                  </ScoreCard>
                </Col>
              )
            )}
            {/* Fill any empty slots in the last row with empty columns for consistent layout */}
            {rowIndex === rows.length - 1 &&
              rowEntries.length < 4 &&
              Array(4 - rowEntries.length)
                .fill()
                .map((_, i) => (
                  <Col key={`rolebased-empty-${i}`} xs={24} sm={12} md={12} lg={6} />
                ))}
          </Row>
        ))}

        {videoDetailedScores.length > 0 && (
          <div style={{ marginBottom: 32 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 12,
                fontSize: 14,
                fontWeight: 600,
                color: '#262626',
              }}
            >
              <span>Video Question Breakdown</span>
              <Tag color="blue">
                {videoDetailedScores.length} {videoDetailedScores.length === 1 ? 'response' : 'responses'}
              </Tag>
              {videoTotalQuestions > videoDetailedScores.length && (
                <Tag color="orange">
                  {videoDetailedScores.length} of {videoTotalQuestions} answered
                </Tag>
              )}
            </div>
            <List
              size="small"
              bordered
              dataSource={videoDetailedScores}
              renderItem={(item, idx) => {
                const score = Number(item.overall_score)
                const scoreText = Number.isFinite(score) ? `${score.toFixed(2)}%` : '-'
                return (
                  <List.Item
                    key={item.question_id || idx}
                    style={{ display: 'block' }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 12,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <Text strong>Video Question {idx + 1}</Text>
                        {item.question_text && (
                          <div style={{ marginTop: 4, color: '#595959' }}>
                            {item.question_text}
                          </div>
                        )}
                      </div>
                      <Tag
                        color={
                          score >= 80 ? 'green' : score >= 60 ? 'blue' : score >= 40 ? 'orange' : 'red'
                        }
                      >
                        {scoreText}
                      </Tag>
                    </div>
                  </List.Item>
                )
              }}
            />
          </div>
        )}
      </>
    )
  }

  // Create custom assessment score cards
  const renderCustomAssessmentScoreCards = () => {
    const customData = customScoresData || student?.sectionScores
    if (!customData || typeof customData !== 'object') {
      // Fallback: show totalScore if available
      if (student?.totalScore != null) {
        return (
          <Row gutter={16} style={{ marginBottom: 32 }}>
            <Col xs={24} sm={12} md={12} lg={6}>
              <ScoreCard score={student.totalScore}>
                <CardContent>
                  <TitleSection>
                    <ScoreTitle level={5}>Overall Score</ScoreTitle>
                    <SubText>(Total Percentage)</SubText>
                  </TitleSection>
                  <ValueSection>
                    <ScoreValue score={student.totalScore}>
                      {student.totalScore}%
                    </ScoreValue>
                  </ValueSection>
                </CardContent>
              </ScoreCard>
            </Col>
          </Row>
        )
      }
      return null
    }
    const sectionWiseStats = customData.sectionWiseStats || customData.sectionwisestats || {}

    // Prepare scores array - Overall percentage first, then sections
    const scores = []

    // Add overall percentage as the first card
    const overallPct = typeof customData.percentage === 'number' ? customData.percentage : (typeof student?.totalScore === 'number' ? student.totalScore : null)
    scores.push({
      key: 'overall',
      title: 'Overall Score',
      subtitle: '(Total Percentage)',
      value: overallPct != null ? overallPct : '-',
      displayValue: overallPct != null ? `${overallPct}%` : '-'
    })

    console.log("Scores", scores)

    // Add section-wise scores
    if (typeof sectionWiseStats === 'object' && Object.keys(sectionWiseStats).length > 0) {
      Object.keys(sectionWiseStats).forEach(sectionName => {
        const section = sectionWiseStats[sectionName]
        scores.push({
          key: section.section_id,
          title: section.section_name,
          subtitle: typeof section.total_attempted === 'number' ? `(${section.total_attempted}/${section.total_questions} Attempted)` : '-',
          value: typeof section.percentage === 'number' ? section.percentage : '-',
          displayValue: typeof section.percentage === 'number' ? `${section.percentage}%` : '-'
        })
      })
    }

    console.log("Scores", scores)
    // Group scores into rows of 4
    const rows = chunkArray(scores, 4)

    return rows.map((rowScores, rowIndex) => (
      <Row
        key={`custom-row-${rowIndex}`}
        gutter={16}
        style={{ marginBottom: rowIndex < rows.length - 1 ? 16 : 32 }}
      >
        {rowScores.map(score => {
          return (
            <Col key={score.key} xs={24} sm={12} md={12} lg={6}>
              <ScoreCard score={score.value}>
                <CardContent>
                  <TitleSection>
                    <ScoreTitle level={5}>{score.title}</ScoreTitle>
                    <SubText>{score.subtitle}</SubText>
                  </TitleSection>
                  <ValueSection>
                    <ScoreValue score={score.value}>
                      {score.displayValue}
                    </ScoreValue>
                  </ValueSection>
                </CardContent>
              </ScoreCard>
            </Col>
          )
        })}
        {/* Fill any empty slots in the last row with empty columns for consistent layout */}
        {/* {rowIndex === rows.length - 1 &&
          rowScores.length < 4 &&
          Array(4 - rowScores.length)
            .fill()
            .map((_, i) => (
              <Col key={`custom-empty-${i}`} xs={24} sm={12} md={12} lg={6} />
            ))} */}
      </Row>
    ))
  }
  // Function to chunk array into groups of n
  const chunkArray = (array, chunkSize) => {
    const result = []
    for (let i = 0; i < array.length; i += chunkSize) {
      result.push(array.slice(i, i + chunkSize))
    }
    return result
  }

  // Create dynamic behavior proficiency cards
  const renderBehaviorScoreCards = () => {
    if (!student?.behaviorProficiencies && !student?.behaviorScores) return null
    const useSource = hasBehaviorObject(student?.behaviorProficiencies)
      ? 'behaviorProficiencies'
      : hasBehaviorObject(student?.behaviorScores)
        ? 'behaviorScores'
        : null
    if (!useSource) {
      const statusLabel =
        (typeof student?.behaviorProficiencies === 'string' && student.behaviorProficiencies) ||
        (typeof student?.behaviorScores === 'string' && student.behaviorScores) ||
        'Pending'
      return (
        <div style={{ padding: '24px 0', textAlign: 'center', color: '#8c8c8c' }}>
          The student has not completed this assessment yet (status: {String(statusLabel).toLowerCase()}).
        </div>
      )
    }
    const behaviorKeys = Object.keys(student[useSource]).sort()
    // Group keys into rows of 4
    const rows = chunkArray(behaviorKeys, 4)
    return rows.map((rowKeys, rowIndex) => (
      <Row
        key={`row-${rowIndex}`}
        gutter={16}
        style={{ marginBottom: rowIndex < rows.length - 1 ? 16 : 32 }}
      >
        {rowKeys.map(key => {
          const value = student[useSource][key]
          const isProficiencyLabel = useSource === 'behaviorProficiencies'
          return (
            <Col key={key} xs={24} sm={12} md={12} lg={6}>
              <ScoreCard score={value}>
                <CardContent>
                  <TitleSection>
                    <ScoreTitle level={5}>{key}</ScoreTitle>
                    <SubText>&nbsp;</SubText>
                  </TitleSection>
                  <ValueSection>
                    <ScoreValue score={value}>
                      {isProficiencyLabel ? (value || '-') : safeToFixed(value)}
                    </ScoreValue>
                  </ValueSection>
                </CardContent>
              </ScoreCard>
            </Col>
          )
        })}
        {/* Fill any empty slots in the last row with empty columns for consistent layout */}
        {rowIndex === rows.length - 1 &&
          rowKeys.length < 4 &&
          Array(4 - rowKeys.length)
            .fill()
            .map((_, i) => (
              <Col key={`empty-${i}`} xs={24} sm={12} md={12} lg={6} />
            ))}
      </Row>
    ))
  }

  // Mid-interview language change (Hinglish interviews only): the candidate said
  // they couldn't follow the Hindi/English mix, so the interview continued in the
  // language they asked for. Shown on both the scored and the not-completed
  // branches — a candidate who switched and then dropped out is exactly the case
  // a recruiter needs the context for. Informational: it never affects the score.
  const renderAIInterviewLanguageSwitch = () => {
    const languageSwitch = aiInterviewReport?.languageSwitch
    if (!languageSwitch?.to) return null
    return (
      <Card style={{ marginBottom: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12 }}>
        <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#b45309', fontWeight: 600, marginBottom: 8 }}>
          Language barrier
        </div>
        <div style={{ fontSize: 14, color: '#0f172a', lineHeight: 1.6 }}>
          The candidate asked to continue in <strong>{languageSwitch.to}</strong> instead of{' '}
          <strong>{languageSwitch.from}</strong>. Every question from that point on was asked in{' '}
          {languageSwitch.to}. This did not affect their score.
        </div>
        {languageSwitch.evidence && (
          <div style={{ fontStyle: 'italic', color: '#475569', borderLeft: '3px solid #f59e0b', padding: '4px 0 4px 12px', marginTop: 8, fontSize: 13 }}>
            “{languageSwitch.evidence}”
          </div>
        )}
      </Card>
    )
  }

  // Candidate identity block — shared by the scored and unscored branches.
  const renderAIInterviewCandidateCard = () => {
    const name = aiInterviewReport?.candidateName || aiInterviewReport?.candidateEmail || 'Candidate'
    const email = aiInterviewReport?.candidateEmail || ''
    return (
      <Card style={{ marginBottom: 12, borderRadius: 12, border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6366f1', fontWeight: 600, marginBottom: 6 }}>
          Candidate
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{name}</div>
        {email && email !== name && (
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{email}</div>
        )}
      </Card>
    )
  }

  // Marker for an interview that answered fewer questions than it expected. It
  // sits ABOVE the score rather than replacing it, because a partial interview
  // that cleared the coverage bar IS scored and the recruiter needs the caveat
  // before they read the number. Below the bar there is no score and the banner
  // plus the attempted questions are the report.
  //
  // Wording AND colour follow how the interview actually ended: amber when the
  // candidate stopped it, neutral slate when the system did (time limit,
  // question limit). See aiInterviewEnding.js.
  const renderAIInterviewIncompleteBanner = () => {
    const isPartial = aiInterviewReport?.partialInterview || aiInterviewReport?.interviewIncomplete
    if (!isPartial) return null
    const answered = aiInterviewReport?.totalAnswered ?? 0
    const expected = aiInterviewReport?.totalExpected ?? '—'
    const hasScore = Boolean(aiInterviewReport?.score)
    // interviewIncomplete means the transcript fell below the coverage bar, so
    // no score will ever be generated for it — distinct from a scorable
    // interview whose score simply hasn't landed yet.
    const isBelowScoringBar = aiInterviewReport?.interviewIncomplete === true
    const ending = describeInterviewEnding(aiInterviewReport?.completionReason)
    return (
      <Card style={{ marginBottom: 12, background: ending.tone.background, border: `1px solid ${ending.tone.border}`, borderRadius: 12 }}>
        <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: ending.tone.label, fontWeight: 600, marginBottom: 8 }}>
          {ending.label}
        </div>
        <div style={{ fontSize: 15, color: '#0f172a', fontWeight: 600, marginBottom: 6 }}>
          {ending.headline}
        </div>
        <div style={{ color: '#475569', fontSize: 13, lineHeight: 1.6 }}>
          {describeScoreImpact({
            answered,
            expected,
            hasScore,
            isCandidateEnded: ending.isCandidateEnded,
            isBelowScoringBar,
          })}
        </div>
      </Card>
    )
  }

  // Questions the candidate actually attempted. Always rendered when a
  // transcript exists — on a partial interview it is the substance of the
  // report, and on a complete one it is the evidence behind the score.
  const renderAIInterviewTranscript = () => {
    const transcript = aiInterviewReport?.transcript
    const answered = Array.isArray(transcript) ? transcript.filter((t) => t.candidateResponse) : []
    if (answered.length === 0 && !callRecordingUrl) return null
    return (
      <Card style={{ marginTop: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6366f1', fontWeight: 600 }}>
            Questions attempted ({answered.length})
          </div>
          {callRecordingUrl && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>Full call recording</span>
              <audio controls preload="none" src={callRecordingUrl} aria-label="Play the full call recording" style={{ height: 32 }} />
            </div>
          )}
        </div>
        {answered.map((turn, i) => (
          <div
            key={turn.interactionId}
            style={{
              padding: '12px 0',
              borderBottom: i < answered.length - 1 ? '1px solid #f1f5f9' : 'none',
            }}
          >
            <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 13, lineHeight: 1.55 }}>
              Q{i + 1}. {turn.questionText}
            </div>
            <div style={{ color: '#334155', fontSize: 13, lineHeight: 1.6, marginTop: 6, whiteSpace: 'pre-wrap' }}>
              {turn.candidateResponse}
            </div>
            {readingFlaggedIds.has(turn.interactionId) && (
              <div style={{ color: '#b45309', fontSize: 12, marginTop: 6 }}>
                Suspected reading detected on this answer.
              </div>
            )}
          </div>
        ))}
      </Card>
    )
  }

  // AI Interview report: Fit Score + admin-defined parameter ratings only.
  // No legacy Reading/Listening/Speaking/Writing cards.
  const renderAIInterviewScoreCards = () => {
    if (aiInterviewLoading) {
      return (
        <Row gutter={16} style={{ marginBottom: 32 }}>
          <Col span={24} style={{ textAlign: 'center', padding: 24 }}>
            <Spin /> <span style={{ marginLeft: 12, color: '#64748b' }}>Loading AI Interview report…</span>
          </Col>
        </Row>
      )
    }
    if (aiInterviewError) {
      return (
        <Row gutter={16} style={{ marginBottom: 32 }}>
          <Col span={24}>
            <Card style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
              <div style={{ color: '#b91c1c' }}>Could not load AI Interview report: {aiInterviewError}</div>
            </Card>
          </Col>
        </Row>
      )
    }
    const score = aiInterviewReport?.score
    if (!score) {
      return (
        <Row gutter={16} style={{ marginBottom: 32 }}>
          <Col span={24}>
            {renderAIInterviewCandidateCard()}
            {renderAIInterviewLanguageSwitch()}
            {renderAIInterviewIncompleteBanner()}
            {!aiInterviewReport?.partialInterview && !aiInterviewReport?.interviewIncomplete && (
              <Empty description="AI Interview not yet scored" />
            )}
            {renderAIInterviewTranscript()}
          </Col>
        </Row>
      )
    }
    // Strong Fit / Fit / Borderline / Not Fit, score-anchored (aiInterviewVerdict.js).
    const effectiveVerdictLabel = effectiveVerdict(score.overallScore, score.verdict)
    const verdictColor = VERDICT_COLORS[effectiveVerdictLabel]
    const stars = (n) => '★'.repeat(Math.max(0, Math.min(5, n || 0))) + '☆'.repeat(5 - Math.max(0, Math.min(5, n || 0)))
    return (
      <div style={{ marginBottom: 32 }}>
        {renderAIInterviewCandidateCard()}

        {renderAIInterviewLanguageSwitch()}

        {renderAIInterviewIncompleteBanner()}

        {/* Fit score header */}
        <Card style={{ marginBottom: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
          <Row gutter={16} align="middle">
            <Col xs={24} md={12}>
              <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6366f1', fontWeight: 600, marginBottom: 6 }}>Fit Score</div>
              <div style={{ fontSize: 36, fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }}>
                {Math.round(score.overallScore || 0)}
                <span style={{ fontSize: 14, color: '#64748b', marginLeft: 4 }}>/ 100</span>
              </div>
              {effectiveVerdictLabel && (
                <div style={{
                  display: 'inline-block',
                  marginTop: 12,
                  padding: '6px 14px',
                  borderRadius: 8,
                  background: verdictColor.bg,
                  color: verdictColor.fg,
                  fontWeight: 600,
                  fontSize: 13,
                }}>
                  {effectiveVerdictLabel}
                </div>
              )}
            </Col>
            <Col xs={24} md={12}>
              {score.executiveSummary && (
                <div>
                  <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6366f1', fontWeight: 600, marginBottom: 6 }}>Executive summary</div>
                  <div style={{ color: '#334155', fontSize: 13, lineHeight: 1.6 }}>{score.executiveSummary}</div>
                </div>
              )}
            </Col>
          </Row>
        </Card>

        {/* Per-parameter ratings — only the admin-defined params, nothing legacy */}
        {Array.isArray(score.parameterScores) && score.parameterScores.length > 0 && (
          <Card style={{ marginBottom: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6366f1', fontWeight: 600, marginBottom: 12 }}>
              Parameter breakdown
            </div>
            {score.parameterScores.map((p, i) => (
              <div key={i} style={{
                display: 'grid',
                gridTemplateColumns: '1.6fr 90px 3fr',
                gap: 12,
                padding: '12px 0',
                borderBottom: i < score.parameterScores.length - 1 ? '1px solid #f1f5f9' : 'none',
                alignItems: 'flex-start',
              }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#0f172a' }}>{p.name}</div>
                  {(p.rating_label || isCommunicationParameter(p)) && (
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      {isCommunicationParameter(p) ? communicationVerdict(p) : p.rating_label}
                    </div>
                  )}
                  {isCommunicationParameter(p) && (
                    <div style={{ fontSize: 15, color: '#0f172a', fontWeight: 700, marginTop: 4 }}>
                      {formatScore(p.score)}
                    </div>
                  )}
                </div>
                <div style={{ color: '#f59e0b', letterSpacing: 2, fontSize: 14 }}>{stars(p.rating)}</div>
                <div style={{ color: '#334155', fontSize: 13, lineHeight: 1.55 }}>
                  {p.analysis}
                  {communicationMetrics(p).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', marginTop: 8 }}>
                      {communicationMetrics(p).map((metric) => (
                        <span key={metric.label} style={{ fontSize: 12, color: '#64748b' }}>
                          {metric.label}: <strong style={{ color: '#334155' }}>{metric.value}</strong>
                        </span>
                      ))}
                    </div>
                  )}
                  {p.supporting_quote && (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#64748b', fontStyle: 'italic', borderLeft: '3px solid #e0e7ff', paddingLeft: 10 }}>
                      &ldquo;{p.supporting_quote}&rdquo;
                    </div>
                  )}
                </div>
              </div>
            ))}
          </Card>
        )}

        {Array.isArray(score.strengths) && score.strengths.length > 0 && (
          <Card style={{ marginBottom: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#059669', fontWeight: 600, marginBottom: 10 }}>Strengths</div>
            {score.strengths.map((s, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>✓ {s.claim || s.summary || s}</div>
                {s.quote && (
                  <div style={{ marginTop: 4, fontSize: 13, color: '#475569', fontStyle: 'italic', borderLeft: '3px solid #6366f1', paddingLeft: 10 }}>
                    &ldquo;{s.quote}&rdquo;
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}

        {Array.isArray(score.concerns) && score.concerns.length > 0 && (
          <Card style={{ marginBottom: 16, borderRadius: 12, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#b45309', fontWeight: 600, marginBottom: 10 }}>Worth probing further</div>
            {score.concerns.map((c, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>⚠ {c.claim || c.summary || c}</div>
                {c.quote && (
                  <div style={{ marginTop: 4, fontSize: 13, color: '#475569', fontStyle: 'italic', borderLeft: '3px solid #f59e0b', paddingLeft: 10 }}>
                    &ldquo;{c.quote}&rdquo;
                  </div>
                )}
                {c.impact && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Impact: {c.impact}</div>}
              </div>
            ))}
          </Card>
        )}

        {score.recommendationText && (
          <Card style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6366f1', fontWeight: 600, marginBottom: 8 }}>Recommendation</div>
            <div style={{ color: '#334155', fontSize: 14, lineHeight: 1.65 }}>{score.recommendationText}</div>
          </Card>
        )}

        {renderAIInterviewTranscript()}
      </div>
    )
  }

  // Submitted, report not scored yet — mirrors corporate's ReportPreparingState.
  const renderReportPreparing = () => {
    const isDelayed = attemptStatusKey === 'reportDelayed'
    return (
      <Card
        role="status"
        style={{
          marginBottom: 32,
          borderRadius: 12,
          border: `1px solid ${isDelayed ? '#fde68a' : '#bae0ff'}`,
          background: isDelayed ? '#fffbeb' : '#f0f7ff',
        }}
      >
        <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 8, color: ATTEMPT_STATUS[attemptStatusKey].color }}>
          {ATTEMPT_STATUS[attemptStatusKey].label}
        </div>
        <div style={{ fontSize: 15, color: '#0f172a', fontWeight: 600, marginBottom: 6 }}>Assessment submitted</div>
        <div style={{ color: '#475569', fontSize: 13, lineHeight: 1.6 }}>
          {isDelayed
            ? 'The candidate has finished their assessment, but their report is taking longer than usual. Check back later. If it still does not appear, contact support.'
            : 'The candidate has finished their assessment. Their report is being prepared and usually takes 1–2 minutes. You can leave this view and check back shortly.'}
        </div>
      </Card>
    )
  }

  // Create aptitude score cards
  const renderAptitudeScoreCards = () => {
    let aptitudeData = null

    // Priority 1: Direct aptitudeScores object
    if (student?.aptitudeScores && typeof student.aptitudeScores === 'object') {
      aptitudeData = { ...student.aptitudeScores }
    }
    // Priority 2: Map from sectionScores if available
    else if (student?.sectionScores && typeof student.sectionScores === 'object') {
      const s = student.sectionScores
      aptitudeData = {}

      const getVal = (k) => {
        const v = s[k]
        if (typeof v === 'object' && v?.average !== undefined) return v.average
        if (typeof v === 'number') return v
        return undefined
      }

      const critical = getVal('critical')
      const quantitative = getVal('quantitative')
      const logical = getVal('logical')

      if (critical !== undefined) aptitudeData.criticalReasoningPercentage = critical
      if (quantitative !== undefined) aptitudeData.quantitativePercentage = quantitative
      if (logical !== undefined) aptitudeData.logicalReasoningPercentage = logical

      if (student.totalAvgScore !== undefined) aptitudeData.overallPercentage = student.totalAvgScore
      else if (student.totalScore !== undefined) aptitudeData.overallPercentage = student.totalScore
    }

    if (!aptitudeData || Object.keys(aptitudeData).length === 0) return null

    // Define the order and display names for aptitude scores
    const scoreMapping = {
      overallPercentage: { title: 'Overall Score', subtitle: '(Total Percentage)' },
      criticalReasoningPercentage: { title: 'Critical Reasoning', subtitle: '(Percentage)' },
      quantitativePercentage: { title: 'Quantitative Ability', subtitle: '(Percentage)' },
      logicalReasoningPercentage: { title: 'Logical Reasoning', subtitle: '(Percentage)' }
    }

    // Get available scores in the defined order. A section the assessment does
    // not include still comes back on aptitudeScores as "-" (admin-node fills
    // the missing category), so drop any section without a real numeric score —
    // only the sections actually selected at creation get a card. Overall keeps
    // the looser presence check so it still renders while scores are pending.
    const availableScores = Object.keys(scoreMapping).filter(key => {
      if (!Object.prototype.hasOwnProperty.call(aptitudeData, key)) return false
      if (key === 'overallPercentage') {
        return aptitudeData[key] !== undefined && aptitudeData[key] !== null
      }
      return hasAptitudeScore(aptitudeData[key])
    })

    // Group scores into rows of 4
    const rows = chunkArray(availableScores, 4)

    return rows.map((rowKeys, rowIndex) => (
      <Row
        key={`aptitude-row-${rowIndex}`}
        gutter={16}
        style={{ marginBottom: rowIndex < rows.length - 1 ? 16 : 32 }}
      >
        {rowKeys.map(key => {
          const value = aptitudeData[key]
          const { title, subtitle } = scoreMapping[key]
          return (
            <Col key={key} xs={24} sm={12} md={12} lg={6}>
              <ScoreCard score={value}>
                <CardContent>
                  <TitleSection>
                    <ScoreTitle level={5}>{title}</ScoreTitle>
                    <SubText>{subtitle}</SubText>
                  </TitleSection>
                  <ValueSection>
                    <ScoreValue score={value}>
                      {safeToFixed(value, 2, '%')}
                    </ScoreValue>
                  </ValueSection>
                </CardContent>
              </ScoreCard>
            </Col>
          )
        })}
        {/* Fill any empty slots in the last row with empty columns for consistent layout */}
        {rowIndex === rows.length - 1 &&
          rowKeys.length < 4 &&
          Array(4 - rowKeys.length)
            .fill()
            .map((_, i) => (
              <Col key={`aptitude-empty-${i}`} xs={24} sm={12} md={12} lg={6} />
            ))}
      </Row>
    ))
  }

  const handleDownloadReport = async (assignedId = null) => {
    if (hasSingleDiagnosisAttempt) {
      message.warning(`${SINGLE_DIAGNOSIS_ATTEMPT_MESSAGE} - the report needs both diagnosis papers`)
      return
    }
    // For non-diagnosis downloads, trust reportAvailable (checkReportAvailability)
    // as the single readiness signal — it already covers scores_calculated for
    // normal types and "a score exists" for AI Interview. Don't re-gate on the
    // raw scoresCalculated flag (it lags for AI interview and blocks a valid PDF).
    if (!assignedId) {
      if (!reportAvailable) {
        message.warning('Report is not available for this assessment')
        return
      }
    }

    try {
      setDownloadingReport(true)
      const API_URL = process.env.STD_API_URL
      const token = localStorage.getItem('token')

      // Add a trailing slash if needed
      const baseUrl = API_URL.endsWith('/') ? API_URL : `${API_URL}/`

      // Use the passed assignedId (for diagnosis dropdown) or fall back to effectiveAssessmentId
      const assessmentAssignedId = assignedId || effectiveAssessmentId

      // Handle different student ID property names
      const rawStudentNumber = student?.studentNumber ?? student?.studentId ?? student?.id
      const studentNumber = (rawStudentNumber !== null && rawStudentNumber !== undefined && rawStudentNumber !== '-') ? rawStudentNumber : null

      if (!assessmentAssignedId || studentNumber === null) {
        message.error('Assessment ID or Student ID is missing')
        setDownloadingReport(false)
        return
      }

      // Build request payload
      const payload = {
        assessment_assigned_id: assessmentAssignedId,
        student_id: studentNumber,
      }

      // Log the request payload for debugging
      //console.log('Sending PDF request with payload:', payload)

      const response = await axios.post(
        `${baseUrl}students/assessments/generatePDFReport`,
        payload,
        {
          // Raw token — backend verifyToken does not strip Bearer.
          headers: { 'Content-Type': 'application/json', Authorization: token },
          responseType: 'blob',
        }
      )

      // Error handling for missing or bad response
      if (!response) {
        message.error('No response from server')
        setDownloadingReport(false)
        return
      }
      if (response.status !== 200) {
        message.error(`Failed to download report`)
        setDownloadingReport(false)
        return
      }
      if (!response.data) {
        message.error('No file data received')
        setDownloadingReport(false)
        return
      }

      let fileName = `${student.id}_report.pdf`
      const contentDisposition = response.headers['content-disposition']
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/)
        if (filenameMatch && filenameMatch[1]) {
          fileName = filenameMatch[1].trim()
        }
      }

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download PDF error:', error)

      // More detailed error reporting
      if (error.response) {
        console.error('Error response:', error.response.data)
        console.error('Status:', error.response.status)
        message.error(`Failed to download report: ${error.response.status} ${error.response.statusText}`)
      } else {
        message.error('Failed to download report')
      }
    } finally {
      setDownloadingReport(false)
    }
  }

  if (!student) return null

  // Read proctoring across ALL sessions of the attempt, not just sessions[0]
  // (see proctoringView.js).
  const proctoringSessions = proctoringData?.sessions || []
  const snapshots = collectSnapshots(proctoringSessions)
  // Backend already aggregates face detection across every session.
  const faceDetectionSummary = proctoringData?.overallSummary || {}
  // const phoneDetectionSummary =  // Phone detection removed
  //   proctoringData?.sessions?.[0]?.phoneDetectionSummary || {}
  const sessionInfo = primarySession(proctoringSessions)
  const ipAddresses = collectIpAddresses(proctoringSessions)

  // Add debug logs RIGHT BEFORE the return statement
  console.log('Proctoring Data:', proctoringData)
  console.log('Snapshots:', snapshots)
  console.log('Face Detection Summary:', faceDetectionSummary)
  console.log('allowProctoring:', allowProctoring)
  console.log('proctoringLoading:', proctoringLoading)
  console.log('Should show proctoring section:', proctoringData && allowProctoring)
  console.log('Display type:', displayType)

  // Get unique IPs
  const uniqueIpData = processIpAddresses(ipAddresses, snapshots)

  return (
    <>
      <Drawer
        visible={visible}
        onClose={onClose}
        placement="right"
        width={720}
        title={null}
        headerStyle={{ display: 'none' }}
        bodyStyle={{ padding: 0, overflowY: 'auto' }}
      >
        {/* Custom header */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          background: '#fff',
          zIndex: 10,
        }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>
            Assessment Report
          </span>
          <span onClick={onClose} style={{ cursor: 'pointer', color: '#8c8c8c', fontSize: 20, lineHeight: 1 }}>×</span>
        </div>
        {/* A Mix & Match float puts its final score and a tab per assessment
            here; everything below is that assessment's own report, unchanged. */}
        {headerExtra}
        <div style={{ padding: '20px 24px' }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '14px 16px',
            background: '#f8f9fc',
            borderRadius: 10,
            border: '1px solid #eef0f6',
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700, color: '#3949ab',
              flexShrink: 0,
              overflow: 'hidden',
            }}>
              {(student.student_img_url || student.profileUrl) ? (
                <img src={student.student_img_url || student.profileUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                (student?.name || student?.studentName || 'S').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#262626', lineHeight: '20px' }}>
                {student?.name || student?.studentName || '-'}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                {[student.email, student.phone].filter(Boolean).join(' · ') || '-'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
              <Tooltip title="Recalculate assessment scores">
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  style={{
                    borderColor: '#3249D7',
                    color: '#3249D7',
                    width: 36,
                    height: 36,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 8,
                    padding: 0,
                  }}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Recalculate Assessment Scores',
                      content: 'Are you sure you want to recalculate the scores for this assessment? This will delete existing scores and recalculate them.',
                      okText: 'Yes, Recalculate',
                      cancelText: 'Cancel',
                      okButtonProps: { style: { backgroundColor: '#3249D7', borderColor: '#3249D7' } },
                      onOk: async () => {
                        if (!effectiveAssessmentId) {
                          message.error('Assessment ID is missing')
                          return
                        }
                        setRecalculating(true)
                        try {
                          const result = await dispatch(recalculateAssessment(effectiveAssessmentId))
                          if (result.success) {
                            message.success('Score recalculation triggered successfully')
                          } else {
                            message.error(result.error || 'Failed to recalculate scores')
                          }
                        } catch (error) {
                          message.error('Failed to recalculate scores')
                        } finally {
                          setRecalculating(false)
                        }
                      },
                    })
                  }}
                  loading={recalculating}
                />
              </Tooltip>
              {/* Disabled buttons swallow mouse events, so the tooltip hangs off a wrapper span */}
              <Tooltip title={downloadReportTitle}>
                <span style={{ display: 'inline-flex' }}>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    style={{
                      whiteSpace: 'nowrap',
                      fontSize: 12,
                      height: 36,
                      padding: '0 14px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: isReportDownloadable ? '#3249D7' : '#9CA3AF',
                      borderColor: isReportDownloadable ? '#3249D7' : '#9CA3AF',
                      borderRadius: 8,
                      boxShadow: isReportDownloadable ? '0 1px 2px rgba(50, 73, 215, 0.2)' : 'none',
                    }}
                    onClick={() => handleDownloadReport()}
                    loading={downloadingReport}
                    disabled={!isReportDownloadable}
                  >
                    Download Report
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>
          {isDiagnosis && (
            <div style={{ marginTop: '16px', marginBottom: '8px' }}>
              <Text strong style={{ marginRight: 8 }}>Select Assessment:</Text>
              <Select
                value={selectedAssessmentId}
                onChange={(value) => setSelectedAssessmentId(value)}
                style={{ width: 280 }}
                placeholder="Select an assessment"
              >
                {student?.assessmentAssignedId1 && (
                  <Option value={student.assessmentAssignedId1}>
                    Assessment #1 {student?.isScoreCalculated1 === false ? '(Calculating...)' : '(Completed)'}
                  </Option>
                )}
                {student?.assessmentAssignedId2 && (
                  <Option value={student.assessmentAssignedId2}>
                    Assessment #2 {student?.isScoreCalculated2 === false ? '(Calculating...)' : '(Completed)'}
                  </Option>
                )}
              </Select>
            </div>
          )}
        </div>        <Divider />

        {/* Latest Score section - Remove the separate View Proctoring button */}
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: '#262626' }}>Latest Score</div>
            {/* Render the media control buttons with Redux-based state */}
            <div style={{ display: 'flex', gap: '8px' }}>
              {!isCallInterview && (
                <Button
                  type="primary"
                  icon={<EyeOutlined />}
                  // disabled={!allowProctoring || isBehaviorAssessment || isAptitudeAssessment}
                  onClick={handleViewProctoring}
                  loading={proctoringLoading}
                >
                  {proctoringData ? 'Refresh Proctoring' : 'View Proctoring'}
                </Button>
              )}
              <Button
                type="primary"
                icon={<AudioFilled />}
                onClick={() => handleMediaView('audio')}
                disabled={mediaLoading || !audioAvailable || isAptitudeAssessment}
                loading={mediaLoading && selectedMediaType === 'audio'}
                title={!audioAvailable || isAptitudeAssessment ? "Audio not available for this assessment" : "View audio recording"}
              />
              <Button
                type="primary"
                icon={<VideoCameraFilled />}
                onClick={() => handleMediaView('video')}
                disabled={mediaLoading || !videoAvailable || isAptitudeAssessment}
                loading={mediaLoading && selectedMediaType === 'video'}
                title={!videoAvailable || isAptitudeAssessment ? "Video not available for this assessment" : "View video recording"}
              />
            </div>
          </div>
          <Text type="secondary">
            Timestamp:{' '}
            {new Date(student.lastAttemptDate || Date.now()).toLocaleString()}
          </Text>
        </div>

        <Divider />

        {/* Proctoring Status section */}
        {/* Only show Proctoring Status for non-behavior and non-aptitude assessments */}
        {/* Aptitude and AI Interview say this inside the integrity panel. */}
        {isProctoringDisabled && !isBehaviorAssessment && !isAptitudeAssessment && !isAIInterviewType && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#262626' }}>Proctoring Status</div>
            <Text type="secondary">Proctoring was disabled for this assessment.</Text>
          </div>
        )}
        {allowProctoring && !isProctoringDisabled && !isCallInterview && !isBehaviorAssessment && !isAptitudeAssessment && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#262626' }}>Proctoring Status</div>
            {/* Readiness: if the integrity report has a real band, it's ready;
                otherwise fall back to the legacy face-detection readiness. */}
            {(!integrityBand || integrityBand === 'no_data') &&
             (student.proctoringCalculated === false || student.flagged === null ||
              student.proctoringStatus === '-' || student.proctoring === '-' || student.proctoringStatus === 'Pending') ? (
              <Badge
                status="processing"
                text={integrityBand === 'no_data' ? 'No proctoring data' : 'Evaluating...'}
              />
            ) : (
              <>
                {/* Flag if EITHER the integrity report (reading-flag verdict
                    — same as the PDF) OR the legacy face-detection rule flags it.
                    This fixes the PDF-says-Needs-Review / UI-says-Good mismatch. */}
                {(() => {
                  const legacyFlagged = student.proctoring === 'Bad' || student.flagged
                  const flagged = integrityFlagged || legacyFlagged
                  // Corporate PROCTORING_CHIP vocabulary: Good / Bad.
                  const label = flagged ? 'Bad' : 'Good'
                  return (
                    <>
                      <Badge status={flagged ? 'error' : 'success'} text={label} />
                      {flagged && (
                        <div style={{ marginTop: 8 }}>
                          <Text type="danger">
                            This assessment was flagged for suspicious activity.
                          </Text>
                        </div>
                      )}
                    </>
                  )
                })()}
              </>
            )}
          </div>
        )}

        {/* Report-only proctoring: after-the-fact integrity report (band,
            metrics, timeline). Never interrupts the candidate.

            Aptitude and AI Interview both finalize one — the interview's is
            written by completeSession — but the panel was gated on aptitude
            alone, so an interview's report was collected, scored and then
            never shown. The panel has its own honest empty state for an
            attempt that captured nothing, so widening the gate cannot put a
            broken card on a report. */}
        {(isAptitudeAssessment || isAIInterviewType) && !isCallInterview && (
          <ProctoringReportPanel
            assessmentAssignedId={effectiveAssessmentId}
            visible={visible}
            isProctoringDisabled={isProctoringDisabled}
          />
        )}

        <Divider />
        {isReportPreparing ? (
          renderReportPreparing()
        ) : isAIInterviewType ? (
          // AI Interview: fit score + admin-defined parameter cards only
          renderAIInterviewScoreCards()
        ) : isBehaviorAssessment ? (
          // Dynamic behavior score cards
          renderBehaviorScoreCards()
        ) : isAptitudeAssessment ? (
          // Dynamic aptitude score cards
          renderAptitudeScoreCards()
        ) : isRoleBasedAssessment ? (
          // Dynamic role-based score cards
          renderRoleBasedScoreCards()
        ) : isCustomAssessment ? (
          // Dynamic custom assessment score cards
          renderCustomAssessmentScoreCards()
        ) : (
          // Language Assessment Cards - supports both sectionScores (new) and flat fields (old)
          <>
            {(() => {
              const sectionScores = student.sectionScores || {}
              const hasNewFormat = Object.keys(sectionScores).length > 0 || student.totalScore !== undefined

              const getScore = (newKey, oldKey) => {
                // Try new format first (sectionScores object)
                if (Object.keys(sectionScores).length > 0) {
                  const val = sectionScores[newKey]
                  if (typeof val === 'object' && val?.average !== undefined) return val.average
                  if (typeof val === 'number') return val
                }
                // Always fall back to flat fields if sectionScores key not found
                return typeof student[oldKey] === 'number' ? student[oldKey] : null
              }

              const totalScore = student.totalScore ?? student.totalAvgScore
              const reading = getScore('reading', 'readingComprehensionScore') ?? getScore('reading', 'readingAbilityScore')
              const listening = getScore('listening', 'listeningAbilityScore')
              const speaking = getScore('speaking', 'englishVerbalScore')
              const writing = getScore('writing', 'writingScore')

              // Normalize enabledSections to group-level
              const SUBSECTION_TO_GROUP = {
                'Paragraph Reading': 'Reading', 'Audio Question': 'Listening',
                'Video Response': 'Speaking', 'Question Based Response': 'Writing',
                'Email Writing': 'Writing', 'Dictation': 'Writing',
                'Sentence Completion': 'Writing', 'Sentence Build': 'Writing',
              };
              const normalizedEnabled = enabledSectionsProp && enabledSectionsProp.length > 0
                ? [...new Set(enabledSectionsProp.map(s => SUBSECTION_TO_GROUP[s] || s))]
                : ['Reading', 'Listening', 'Speaking', 'Writing'];
              const isEnabled = (section) => normalizedEnabled.includes(section);

              return (
                <Row gutter={16} style={{ marginBottom: 32 }}>
                  <Col span={6}>
                    <ScoreCard score={totalScore ?? 0}>
                      <CardContent>
                        <TitleSection>
                          <ScoreTitle level={5}>Total Score</ScoreTitle>
                          <SubText>(Average of all sections)</SubText>
                        </TitleSection>
                        <ValueSection>
                          <ScoreValue score={totalScore ?? 0}>
                            {typeof totalScore === 'number' ? totalScore.toFixed(2) : '-'}
                          </ScoreValue>
                        </ValueSection>
                      </CardContent>
                    </ScoreCard>
                  </Col>
                  {isEnabled('Reading') && (
                    <Col span={6}>
                      <ScoreCard score={reading ?? 0}>
                        <CardContent>
                          <TitleSection>
                            <ScoreTitle level={5}>Reading</ScoreTitle>
                            <SubText>(Comprehension)</SubText>
                          </TitleSection>
                          <ValueSection>
                            <ScoreValue score={reading ?? 0}>
                              {typeof reading === 'number' ? reading.toFixed(2) : '-'}
                            </ScoreValue>
                          </ValueSection>
                        </CardContent>
                      </ScoreCard>
                    </Col>
                  )}
                  {isEnabled('Listening') && (
                    <Col span={6}>
                      <ScoreCard score={listening ?? 0}>
                        <CardContent>
                          <TitleSection>
                            <ScoreTitle level={5}>Listening</ScoreTitle>
                            <SubText>&nbsp;</SubText>
                          </TitleSection>
                          <ValueSection>
                            <ScoreValue score={listening ?? 0}>
                              {typeof listening === 'number' ? listening.toFixed(2) : '-'}
                            </ScoreValue>
                          </ValueSection>
                        </CardContent>
                      </ScoreCard>
                    </Col>
                  )}
                  {isEnabled('Speaking') && (
                    <Col span={6}>
                      <ScoreCard score={speaking ?? 0}>
                        <CardContent>
                          <TitleSection>
                            <ScoreTitle level={5}>Speaking</ScoreTitle>
                            <SubText>&nbsp;</SubText>
                          </TitleSection>
                          <ValueSection>
                            <ScoreValue score={speaking ?? 0}>
                              {typeof speaking === 'number' ? speaking.toFixed(2) : '-'}
                            </ScoreValue>
                          </ValueSection>
                        </CardContent>
                      </ScoreCard>
                    </Col>
                  )}
                  {isEnabled('Writing') && writing !== null && typeof writing === 'number' && (
                    <Col span={6} style={{ marginTop: 16 }}>
                      <ScoreCard score={writing}>
                        <CardContent>
                          <TitleSection>
                            <ScoreTitle level={5}>Writing</ScoreTitle>
                            <SubText>&nbsp;</SubText>
                          </TitleSection>
                          <ValueSection>
                            <ScoreValue score={writing}>
                              {writing.toFixed(2)}
                            </ScoreValue>
                          </ValueSection>
                        </CardContent>
                      </ScoreCard>
                    </Col>
                  )}
                </Row>
              )
            })()}
          </>
        )}

        {/* Progression Graphs - show only relevant type */}
        {(commProgression.length > 0 || aptProgression.length > 0) && (
          <>
            <Divider />
            <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', marginBottom: 16 }}>
              {isCommunicationType ? 'Communication Improvement' : isAptitudeType ? 'Aptitude Improvement' : 'Improvement'}
            </div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              {commProgression.length > 0 && !isAptitudeType && (
                <Col span={aptProgression.length > 0 && !isCommunicationType ? 12 : 24}>
                  <ProgressionGraph
                    data={commProgression}
                    title="Communication Improvement"
                    currentAssessmentId={effectiveAssessmentId}
                    delta={commDelta?.value}
                    height={250}
                    showPractice={false}
                  />
                </Col>
              )}
              {aptProgression.length > 0 && !isCommunicationType && (
                <Col span={commProgression.length > 0 && !isAptitudeType ? 12 : 24}>
                  <ProgressionGraph
                    data={aptProgression}
                    title="Aptitude Improvement"
                    currentAssessmentId={effectiveAssessmentId}
                    delta={aptDelta?.value}
                    height={250}
                    showPractice={false}
                  />
                </Col>
              )}
            </Row>
          </>
        )}

        {/* Proctoring Snapshots Section - Only show for Communication assessments */}
        {proctoringLoading ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Spin size="large" tip="Loading proctoring data..." />
          </div>
        ) : proctoringData && proctoringData.proctoringPending ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Text strong>{proctoringData.message}</Text>
                  <Badge status="processing" text="Calculation in progress" />
                </div>
              }
            />
          </div>
        ) : proctoringData ? (
          <>
            <Divider />
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', marginRight: 12 }}>
                  Proctoring Data
                </div>
                <Select
                  value={displayType}
                  onChange={handleDisplayTypeChange}
                  style={{ width: 180 }}
                >
                  <Option value="snapshots">Snapshots</Option>
                  <Option value="ipAddresses">IP Addresses</Option>
                </Select>
              </div>
            </div>

            {/* Conditional rendering based on selected display type */}
            {displayType === 'snapshots' ? (
              <>
                {/* Face Detection Statistics */}
                {faceDetectionSummary && Object.keys(faceDetectionSummary).length > 0 && (
                  <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    {/* Face Detection Statistics */}
                    <Col span={24}>
                      <Title level={5}>Face Detection Summary</Title>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card>
                        <Statistic
                          title="Face Detection Rate"
                          value={faceDetectionSummary.faceDetectionRate || 'N/A'}
                          valueStyle={{
                            color:
                              faceDetectionSummary.faceDetectionRate &&
                                parseInt(faceDetectionSummary.faceDetectionRate) < 80
                                ? '#E35E50'
                                : '#0FB680',
                            fontWeight: 'bold',
                          }}
                        />
                      </Card>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                      <Card>
                        <Statistic
                          title="Single Face (Valid)"
                          value={faceDetectionSummary.singleFace || 0}
                          valueStyle={{ color: '#0FB680' }}
                          suffix={`/ ${faceDetectionSummary.totalSnapshots || 0}`}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card>
                        <Statistic
                          title="Multiple/No Faces"
                          value={
                            (faceDetectionSummary.multipleFaces || 0) +
                            (faceDetectionSummary.noFaceDetected || 0)
                          }
                          valueStyle={{ color: '#E35E50' }}
                          suffix={`/ ${faceDetectionSummary.totalSnapshots || 0}`}
                        />
                      </Card>
                    </Col>
                  </Row>
                )}

                {/* Phone Detection Summary — removed */}

                {/* Snapshots Grid */}
                {snapshots && snapshots.length > 0 ? (
                  <Row gutter={[16, 16]}>
                    {snapshots.map((snapshot, index) => (
                      <Col span={8} key={snapshot.id}>
                        <SnapshotCard>
                          {snapshot.imageUrl?.data?.parUrl ? (
                            <Image
                              className="snapshot-image"
                              src={snapshot.imageUrl.data.parUrl}
                              alt={`Snapshot ${index + 1}`}
                              style={{
                                width: '100%',
                                height: '150px',
                                objectFit: 'cover',
                              }}
                              preview={{
                                mask: 'View Image'
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                height: 150,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px'
                              }}
                            >
                              <Empty
                                description={
                                  snapshot.expired
                                    ? 'Expired — retention policy'
                                    : 'No image'
                                }
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                              />
                            </div>
                          )}
                          <div className="snapshot-info">
                            <div>
                              <Text strong>Time: </Text>
                              <Text>{formatDateTime(snapshot.capturedAt)}</Text>
                            </div>
                            <div>
                              <Text strong>Face Count: </Text>
                              {snapshot.faceCount === 1 ? (
                                <Text type="success">
                                  <CheckCircleFilled /> {snapshot.faceCount} (Valid)
                                </Text>
                              ) : snapshot.faceCount === 0 ? (
                                <Text type="danger">
                                  <CloseCircleFilled /> None
                                </Text>
                              ) : snapshot.faceCount > 1 ? (
                                <Text type="warning">
                                  <CloseCircleFilled /> {snapshot.faceCount} (Multiple)
                                </Text>
                              ) : (
                                // -1 = queued for server CV, -2 = being processed.
                                <Text type="secondary">Processing…</Text>
                              )}
                            </div>
                            {/* Phone detection removed */}
                          </div>
                        </SnapshotCard>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Empty description="No snapshots available" />
                )}
              </>
            ) : (
              // Display IP information
              <div>
                {sessionInfo && Object.keys(sessionInfo).length > 0 && (
                  <Card style={{ marginBottom: 16 }}>
                    <div style={{ marginTop: 8 }}>
                      <Text strong style={{ marginRight: 8 }}>
                        Assessment Status:
                      </Text>
                      <Tag
                        color={
                          proctoringData.assessmentStatus === 'Completed'
                            ? 'success'
                            : 'processing'
                        }
                      >
                        {proctoringData.assessmentStatus || 'N/A'}
                      </Tag>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Text strong style={{ marginRight: 8 }}>
                        Browser:
                      </Text>
                      <Text>{sessionInfo.browserInfo || 'N/A'}</Text>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Text strong style={{ marginRight: 8 }}>
                        Session Duration:
                      </Text>
                      <Text>{sessionInfo.sessionDuration || 'N/A'}</Text>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Text strong style={{ marginRight: 8 }}>
                        Session Start:
                      </Text>
                      <Text>
                        {sessionInfo.sessionStart
                          ? formatDateTime(sessionInfo.sessionStart)
                          : 'N/A'}
                      </Text>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Text strong style={{ marginRight: 8 }}>
                        Session End:
                      </Text>
                      <Text>
                        {sessionInfo.sessionEnd
                          ? formatDateTime(sessionInfo.sessionEnd)
                          : 'N/A'}
                      </Text>
                    </div>
                  </Card>
                )}

                <Card title="IP Addresses" extra={<GlobalOutlined />}>
                  {uniqueIpData && uniqueIpData.length > 0 ? (
                    <Table
                      dataSource={uniqueIpData}
                      columns={[
                        {
                          title: 'IP Address',
                          dataIndex: 'ip',
                          key: 'ip',
                          render: text => <Text copyable>{text}</Text>,
                        },
                        {
                          title: 'First Detected',
                          dataIndex: 'timestamp',
                          key: 'timestamp',
                        },
                        {
                          title: 'Occurrence',
                          dataIndex: 'count',
                          key: 'count',
                          render: text => (text > 1 ? `${text} times` : '1 time'),
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                  ) : (
                    <Empty description="No IP address data available" />
                  )}
                </Card>
              </div>
            )}
          </>
        ) : null}


        </div>
      </Drawer>

      <AudioVideoModal
        visible={audioVideoVisible}
        onClose={handleAudioVideoModalClose}
        mediaType={selectedMediaType}
        audioUrl={mediaUrls?.audio}
        videoUrl={mediaUrls?.video}
        loading={mediaLoading}
      />
    </>
  )
}

export default StudentReportModal
